import parse from 'date-fns/parse';
import format from 'date-fns/format';

import { validateQuestion } from 'components/Questionnaires/validators';

const checkLock = (question, formSettings) => {
  if (question.lockGroup && formSettings) {
    question.disabled = formSettings[question.lockGroup] === true;
  }
};

const populateQuestion = (questionnaire, blockId, questionId, value, lockSettings) => {
  if (value) {
    const block = questionnaire.blocks.find(x => x.id === blockId);
    if (block) {
      block.answers = block.answers ?? {};
      const question = block.questions.find(x => x.id === questionId);
      if (question) {
        checkLock(question, lockSettings?.formSettings);

        question.answer = value;
        validateQuestion(question);
        block.answers[questionId] = value;
        block.isValid = block.questions?.every(q => q.isValid === undefined || q.isValid === true) ?? true;
      }
      questionnaire.answers[blockId] = block.answers;
    }
  }
};

const populateNameParts = (questionnaire, blockId, name, lockSettings) => {
  if (name) {
    const nameBlock = questionnaire.blocks.find(x => x.id === blockId);
    if (nameBlock) {
      nameBlock.answers = nameBlock.answers ?? {};
      const firstSpaceIndex = name.indexOf(' ');
      const lastSpaceIndex = name.lastIndexOf(' ');

      const firstName = nameBlock.questions.find(x => x.id === 'firstName' && x.component.displayName === 'QuestionTextField');
      if (firstName) {
        checkLock(firstName, lockSettings?.formSettings);
        if (firstSpaceIndex === -1) {
          firstName.answer = name;
        } else {
          firstName.answer = name.substring(0, firstSpaceIndex).trim();
        }
        validateQuestion(firstName);
        nameBlock.answers.firstName = firstName.answer;
      }

      const middleName = nameBlock.questions.find(x => x.id === 'middleName' && x.component.displayName === 'QuestionTextField');
      if (middleName) {
        checkLock(middleName, lockSettings?.formSettings);
        if (firstSpaceIndex !== -1 && lastSpaceIndex !== -1 && firstSpaceIndex !== lastSpaceIndex) {
          middleName.answer = name.substring(firstSpaceIndex + 1, lastSpaceIndex).trim();
          validateQuestion(middleName);
          nameBlock.answers.middleName = middleName.answer;
        }
      }

      const lastName = nameBlock.questions.find(x => x.id === 'lastName' && x.component.displayName === 'QuestionTextField');
      if (lastName) {
        checkLock(lastName, lockSettings?.formSettings);
        if (lastSpaceIndex !== -1) {
          lastName.answer = name.substring(lastSpaceIndex).trim();
          validateQuestion(lastName);
          nameBlock.answers.lastName = lastName.answer;
        }
      }

      const title = nameBlock.questions.find(x => x.id === 'title' && x.component.displayName === 'QuestionDropDownField');
      if (title) {
        checkLock(title, lockSettings?.formSettings);
      }

      nameBlock.isValid = nameBlock.questions?.every(q => q.isValid === undefined || q.isValid === true) ?? true;
      questionnaire.answers[blockId] = nameBlock.answers;
    }
  }
};

const populateAddress = (questionnaire, blockId, address, lockSettings) => {
  if (address && (address.line1 || address.line2 || address.line3 || address.line4
    || address.postalCode || address.country)) {
    const addressBlock = questionnaire.blocks.find(x => x.id === blockId && x.component.displayName === 'QuestionAddressBlock');
    if (addressBlock) {
      checkLock(addressBlock, lockSettings?.formSettings);
      addressBlock.answers = Object.assign({}, address);
      questionnaire.answers[blockId] = addressBlock.answers;
    }
  }
};

const populateBirthData = (questionnaire, blockId, birthDate, birthCountry, lockSettings) => {
  const birthBlock = questionnaire.blocks.find(x => x.id === blockId);
  if (!birthBlock) {
    return;
  }

  birthBlock.answers = birthBlock.answers ?? {};
  if (birthDate) {
    const date = birthBlock.questions.find(x => x.id === 'date' && x.component.displayName === 'QuestionDateDropdownField');
    if (date) {
      checkLock(date, lockSettings?.formSettings);
      const parsed = parse(birthDate, 'MM-dd-yyyy', new Date());
      const formattedDate = format(parsed, date.componentProps?.dateFormat ?? 'MM-dd-yyyy');
      date.answer = formattedDate;
      validateQuestion(date);
      birthBlock.answers.date = formattedDate;
    }
  }

  if (birthCountry) {
    const country = birthBlock.questions.find(x => x.id === 'country' && x.component.displayName === 'QuestionJurisdictionDropDownField');
    if (country) {
      checkLock(country, lockSettings?.formSettings);
      country.answer = birthCountry;
      validateQuestion(country);
      birthBlock.answers.country = country;
    }
  }
  birthBlock.isValid = birthBlock.questions?.every(q => q.isValid === undefined || q.isValid === true) ?? true;
  questionnaire.answers[blockId] = birthBlock.answers;
};

export const prePopulateAnswers = (questionnaire, accountId, customerData, lockSettings) => {
  if (!questionnaire || !questionnaire.answers || !customerData) {
    return;
  }

  populateQuestion(questionnaire, 'name', 'name', customerData.CustomerName, lockSettings);
  populateNameParts(questionnaire, 'name', customerData.CustomerName, lockSettings);
  populateQuestion(questionnaire, 'citizenshipCountry', 'country', customerData.CountryOfCitizenship, lockSettings);

  populateAddress(questionnaire, 'address', {
    line1: customerData.FatcaAddress?.Line1,
    line2: customerData.FatcaAddress?.Line2,
    line3: customerData.FatcaAddress?.Line3,
    line4: customerData.FatcaAddress?.Line4,
    postalCode: customerData.FatcaAddress?.PostCode,
    country: customerData.FatcaAddress?.Country
  }, lockSettings);

  populateAddress(questionnaire, 'mailingAddress', {
    line1: customerData.MailingAddress?.Line1,
    line2: customerData.MailingAddress?.Line2,
    line3: customerData.MailingAddress?.Line3,
    line4: customerData.MailingAddress?.Line4,
    postalCode: customerData.MailingAddress?.PostCode,
    country: customerData.MailingAddress?.Country
  }, lockSettings);

  populateBirthData(questionnaire, 'birth', customerData.DateOfBirth, customerData.CountryOfBirth, lockSettings);
};

export default prePopulateAnswers;
